import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import { Page, message } from 'components';
import { debounce, throttle } from 'lodash';

import { getListingAccessActivities } from 'api/get';
import { LockboxesActivityFilterOptions } from 'api/get/getListingAccessActivities';
import { useIsFirstRender } from 'hooks';
import { SupraLockboxActivity } from '../LockboxesActivityPage/types';
import LockboxesActivityRightHeader from '../LockboxesActivityPage/LockboxesActivityRightHeader';
import LockboxActivityTable from '../LockboxesActivityPage/LockboxActivityTable';
import AccessActivityBreadcrumb from './AccessActivityBreadcrumb';

type AccessActivityPageProps = {
  listingId: string;
};

const AccessActivityPage = ({ listingId }: AccessActivityPageProps) => {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const isFirstRender = useIsFirstRender();

  const [total, setTotal] = useState(0);
  const [initialTotal, setInitialTotal] = useState(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<LockboxesActivityFilterOptions>({
    ownerOrAssignee: [],
    ownerIds: [],
    assignee: [],
    lockboxIds: [],
    activities: [],
    lockboxSerialNumbers: [],
    startDate: null,
    endDate: null,
    userIds: [],
    keyTypes: [],
    search: '',
  });

  const [sortOptions, setSortOptions] = useState<{
    sort: string;
    order: 'descend' | 'ascend';
  }>({
    sort: '',
    order: 'descend',
  });

  const [lockboxActivities, setLockboxActivities] = useState<
    SupraLockboxActivity[]
  >([]);

  useEffect(() => {
    if (search) {
      // eslint-disable-next-line no-underscore-dangle
      const _lockboxIds = searchParams
        .get('lockboxIds')
        ?.split(',')
        .filter(Boolean);
      setFilters(prev => ({
        ...prev,
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate(),
        lockboxIds: _lockboxIds,
      }));
    }
  }, [search, searchParams]);

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    const callId = Math.random().toString(16);
    (async () => {
      try {
        setLoading(prev => ({
          ...prev,
          [callId]: true,
        }));
        const { activities, count } = await getListingAccessActivities({
          listingId,
          filters,
          page,
          cancelToken: cancelToken.token,
        });
        setLockboxActivities(activities);
        setTotal(count);
        if (!initialTotal) setInitialTotal(count);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(prev => ({
          ...prev,
          [callId]: false,
        }));
      }
    })();

    return () => cancelToken.cancel();
  }, [filters, sortOptions, page]);

  useEffect(() => () => {
      debouncedSearch.cancel();
    }, []);

  useEffect(() => {
    if (isFirstRender) return;
    debouncedSearch(searchText);
  }, [searchText]);

  const debouncedError = useCallback(
    throttle(msg => message.error(msg), 1500, { trailing: false }),
    [],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(text => {
        setFilters(prev => ({ ...prev, search: text }));
      }, 300),
    [filters],
  );

  const isLoading = useMemo(() => Object.values(loading).reduce(
      (acc, _loading) => acc || _loading,
      false,
    ), [loading]);

  const handleError = (err: any) => {
    if (axios.isCancel(err)) return;
    if (err?.response?.status === 403) {
      debouncedError('You do not have access to this page');
    } else {
      debouncedError('Something went wrong');
    }
  };

  return (
    <Page
      title="Access Activity"
      showBackButton
      type="filled"
      overTitle={<AccessActivityBreadcrumb />}
    >
      <LockboxesActivityRightHeader
        filters={filters}
        sort={sortOptions}
        page={page}
        setPage={setPage}
        total={total}
        initialTotal={initialTotal}
        searchText={searchText}
        setSearchText={setSearchText}
        setFilters={setFilters}
        isLoading={false}
        listingAccessActivityProps={{ listingId }}
      />
      <LockboxActivityTable
        filters={filters}
        setFilters={setFilters}
        sortOptions={sortOptions}
        setSortOptions={setSortOptions}
        page={page}
        setPage={setPage}
        activities={lockboxActivities}
        loading={isLoading}
        totalCount={total}
        listingAccessActivityProps={{ listingId }}
      />
    </Page>
  );
};

export default AccessActivityPage;
